/**
 * AddStopPopup — from any dispatch card, add another WO (approved / ready queue — same rule as
 * drag-to-schedule) or any contract job to that tech's day.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { listContractJobs } from "@/lib/api";
import { JobTag, kindMeta } from "@/components/dispatch/JobTag";

const RECENT_COUNT = 3;

export default function AddStopPopup({ open, onOpenChange, techName, date, queue = [], approvedQueue = [], excludeWoIds = [], excludeContractIds = [], onPick, onNewContractJob, kind }) {
  const meta = kindMeta(kind);
  const showWos = !kind || kind === "contract";
  const [q, setQ] = useState("");
  const [contractJobs, setContractJobs] = useState([]);
  const [endDate, setEndDate] = useState("");
  useEffect(() => {
    if (!open) return;
    setQ(""); setEndDate("");
    listContractJobs().then(r => setContractJobs(r.data || [])).catch(() => setContractJobs([]));
  }, [open]);
  const needle = q.trim().toLowerCase();
  const hit = (...vals) => !needle || vals.some(v => (v || "").toString().toLowerCase().includes(needle));
  const wos = useMemo(() => {
    const seen = new Set();
    return [...queue, ...approvedQueue].filter(w => {
      if (!w?.id || seen.has(w.id) || excludeWoIds.includes(w.id)) return false;
      seen.add(w.id);
      return hit(w.wo_number, w.title, w.client_name, w.site_name, w.site_city);
    });
  }, [queue, approvedQueue, excludeWoIds, needle]); // eslint-disable-line react-hooks/exhaustive-deps
  const cjsAll = contractJobs.filter(j => (!kind || (j.job_kind || "contract") === kind) && !excludeContractIds.includes(j.id) && hit(j.name, j.city, j.site_address));
  const recentIds = [...cjsAll].filter(j => j.last_dispatched_at).sort((a, b) => b.last_dispatched_at.localeCompare(a.last_dispatched_at)).slice(0, RECENT_COUNT).map(j => j.id);
  const recent = recentIds.map(id => cjsAll.find(j => j.id === id));
  const cjs = cjsAll.filter(j => !recentIds.includes(j.id));
  const range = endDate && endDate > date ? endDate : null;
  const contractRow = (j, isRecent) => (
    <button key={j.id} type="button" onClick={() => onPick({ kind: "contract", item: j, endDate: range })}
      className="w-full flex items-center gap-2 rounded-md border border-slate-200 hover:border-blue-400 hover:bg-blue-50 px-2 py-1.5 text-left mb-1"
      data-testid={`add-stop-${isRecent ? "recent" : "contract"}-${j.id}`}>
      <JobTag kind={j.job_kind || "contract"} />
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-slate-800 truncate">{j.name}</span>
        <span className="block text-[11px] text-slate-500 truncate">{j.site_address || "No address"}</span>
      </span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px] max-h-[88vh] flex flex-col" data-testid="add-stop-popup">
        <DialogHeader>
          <DialogTitle className="text-base">{kind ? `Add ${meta.label}` : "Add stop"}</DialogTitle>
          <p className="text-xs text-slate-500">{techName} · {date}. {kind && !showWos ? `Pick a ${meta.label.toLowerCase()} or create a new one.` : "Pick a work order from the dispatch queue or a contract job."}</p>
        </DialogHeader>
        <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={showWos ? "Search WO #, title, customer, contract job…" : `Search ${meta.label.toLowerCase()}…`} data-testid="add-stop-search" />
        <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="add-stop-range">
          <span className="whitespace-nowrap">{kind ? meta.label : "Contract job"} through</span>
          <Input type="date" min={date} value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 w-40 text-xs" data-testid="add-stop-end-date" />
          <span className="text-[11px] text-slate-400">{range ? "weekdays · off days skipped" : "optional · single day"}</span>
        </label>
        <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-3 text-sm">
          <section>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{kind ? meta.label : "Contract · Overhead · Warranty"}</div>
              {onNewContractJob && (
                <button type="button" onClick={() => onNewContractJob(range, kind || "contract")} className={`text-[11px] hover:underline flex items-center gap-0.5 ${meta.textCls}`} data-testid="add-stop-new-contract">
                  <Plus className="w-3 h-3" />New {(kind ? meta.label : "contract job").toLowerCase()}
                </button>
              )}
            </div>
            {recent.length > 0 && (
              <div className="mb-2" data-testid="add-stop-recent">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Recent</div>
                {recent.map(j => contractRow(j, true))}
              </div>
            )}
            {cjsAll.length === 0 && <div className="text-xs text-slate-400 italic">No {(kind ? meta.label : "contract jobs").toLowerCase()}{needle ? " match" : " yet"}.</div>}
            {cjs.map(j => contractRow(j, false))}
          </section>
          {showWos && <section>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Work orders (dispatch queue)</div>
            {wos.length === 0 && <div className="text-xs text-slate-400 italic">No ready work orders{needle ? " match" : ""}.</div>}
            {wos.map(w => (
              <button key={w.id} type="button" onClick={() => onPick({ kind: "service", item: w })}
                className="w-full flex items-center gap-2 rounded-md border border-slate-200 hover:border-slate-500 hover:bg-slate-50 px-2 py-1.5 text-left mb-1"
                data-testid={`add-stop-wo-${w.id}`}>
                <JobTag kind="service" />
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-slate-800 truncate"><span className="text-blue-600 mr-1">{w.wo_number}</span>{w.title}</span>
                  <span className="block text-[11px] text-slate-500 truncate">{w.client_name}{w.site_city ? ` · ${w.site_city}` : ""}{w.remaining_hours > 0 ? ` · ${w.remaining_hours}h left` : ""}</span>
                </span>
              </button>
            ))}
          </section>}
        </div>
        <div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="add-stop-close">Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}
